import {SignatureOutputFormat} from "../types";
import * as React from "react";
import {MutableRefObject, useCallback, useEffect, useRef, useState} from "react";
import SignaturePad, {PointGroup} from "signature_pad";
import {
    classNames,
    layout,
    PushButton,
    typography,
    View,
    ViewContent,
    ViewFooter,
    ViewFooterItem,
    ViewHeader,
    ViewHeaderItem
} from "@variocube/cube-ui";
import {stylesheet} from "typestyle";
import {useLocalization} from "../../i18n";

const styles = stylesheet({
    canvas: {
        width: "100%",
        height: "100%",
        border: "1px solid #666"
    }
});

export interface SignatureViewProps {
    strokeWidth: StrokeWidth;
    onNext: (signature: string) => void;
    onCancel: () => void;
    outputFormat?: SignatureOutputFormat;
}

export interface StrokeWidth {
    minWidth: number;
    maxWidth: number;
}

export function SignatureView({strokeWidth, outputFormat, onNext, onCancel}: SignatureViewProps) {
    const {t} = useLocalization();

    const [signature, setSignature] = useState("");
    const [signaturePointsList, setSignaturePointsList] = useState<PointGroup[]>();
    const clearRef = useRef<() => void>();

    const handleClearClick = useCallback(() => {
        setSignature("");
        setSignaturePointsList(undefined);
        if (clearRef.current) {
            clearRef.current();
        }
    }, []);

    const handleNextClick = useCallback(() => {
        if (outputFormat === "JsonPointList" && signaturePointsList) {
            const xCoordinates = signaturePointsList.map(entry => entry.points.map(point => Math.floor(point.x)));
            const yCoordinates = signaturePointsList.map(entry => entry.points.map(point => Math.floor(point.y)));
            const pointsList = xCoordinates.map((x, index) => ({x, y: yCoordinates[index]}));
            onNext(JSON.stringify(pointsList));
        } else if (signature) {
            onNext(signature);
        }
    }, [onNext, signature]);

    const handleSignatureChange = useCallback((canvas: HTMLCanvasElement, empty: boolean, points: PointGroup[]) => {
        setSignature(empty ? "" : canvas.toDataURL("image/png"));
        setSignaturePointsList(points);
    }, []);

    return (
        <View>
            <ViewHeader>
                <ViewHeaderItem className={layout.paddingMedium}>
                    <div className={typography.title}>
                        {t("Signature")}
                    </div>
                    <div className={typography.description}>
                        {t("SignatureDescription")}
                    </div>
                </ViewHeaderItem>
            </ViewHeader>
            <ViewContent className={layout.paddingMedium}>
                <SignatureCanvas
                    strokeWidth={strokeWidth}
                    className={styles.canvas}
                    onSignatureChange={handleSignatureChange}
                    clearRef={clearRef}
                />
            </ViewContent>
            <ViewFooter className={classNames(layout.spaceBetween, layout.paddingMedium)}>
                <ViewFooterItem>
                    <PushButton onClick={onCancel}>
                        {t("Cancel")}
                    </PushButton>
                </ViewFooterItem>
                <ViewFooterItem>
                    <PushButton onClick={handleClearClick}>
                        {t("Clear")}
                    </PushButton>
                </ViewFooterItem>
                <ViewFooterItem>
                    <PushButton onClick={handleNextClick} primary disabled={!signature}>
                        {t("Next")}
                    </PushButton>
                </ViewFooterItem>
            </ViewFooter>
        </View>
    );
}

interface SignatureCanvasProps extends React.DetailedHTMLProps<React.CanvasHTMLAttributes<HTMLCanvasElement>, HTMLCanvasElement> {
    onSignatureChange: (canvas: HTMLCanvasElement, empty: boolean, points: PointGroup[]) => void;
    clearRef?: MutableRefObject<(() => void) | undefined>;
    strokeWidth: StrokeWidth;
}

function SignatureCanvas({onSignatureChange, clearRef, strokeWidth, ...canvasProps}: SignatureCanvasProps) {
    const signaturePadRef = useRef<SignaturePad | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    const notifySignatureChange = useCallback(() => {
        if (canvasRef.current && signaturePadRef.current) {
            onSignatureChange(canvasRef.current, signaturePadRef.current.isEmpty(), signaturePadRef.current.toData());
        }
    }, [onSignatureChange]);

    const handleClear = useCallback(() => {
        signaturePadRef.current?.clear();
    }, []);

    useEffect(() => {
        if (clearRef) {
            clearRef.current = handleClear;
        }
    }, [clearRef, handleClear]);

    const handleResize = useCallback(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            // When zoomed out to less than 100%, for some very strange reason,
            // some browsers report devicePixelRatio as less than 1
            // and only part of the canvas is cleared then.
            const ratio = Math.max(window.devicePixelRatio || 1, 1);

            // This part causes the canvas to be cleared
            canvas.width = canvas.offsetWidth * ratio;
            canvas.height = canvas.offsetHeight * ratio;
            canvas.getContext("2d")?.scale(ratio, ratio);

            // This library does not listen for canvas changes, so after the canvas is automatically
            // cleared by the browser, SignaturePad#isEmpty might still return false, even though the
            // canvas looks empty, because the internal data of this library wasn't cleared. To make sure
            // that the state of this library is consistent with visual state of the canvas, you
            // have to clear it manually.
            signaturePadRef.current?.clear();

            notifySignatureChange();
        }
    }, [notifySignatureChange]);

    useEffect(() => {
        if (canvasRef.current) {
            const pad = new SignaturePad(canvasRef.current, {
                minWidth: strokeWidth.minWidth,
                maxWidth: strokeWidth.maxWidth
            });
            pad.onEnd = notifySignatureChange;
            signaturePadRef.current = pad;
            handleResize();
            notifySignatureChange();
        }
    }, [strokeWidth, handleResize, notifySignatureChange]);

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [handleResize]);

    return (
        <canvas {...canvasProps} ref={canvasRef}/>
    );
}